import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import BMIGauge from "../components/BMIGauge";
import ReferralCard from "../components/ReferralCard";
import RecommendedReading from "../components/RecommendedReading";
import CheckinForm from "../components/CheckinForm";
import ProgressChart from "../components/ProgressChart";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { track } from "../lib/analytics";

const levelCls = {
  low: "bg-emerald-100 text-emerald-800",
  moderate: "bg-amber-100 text-amber-800",
  high: "bg-red-100 text-red-700",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState([]);
  const [whatsapp, setWhatsapp] = useState("");
  const [checkins, setCheckins] = useState([]);

  const loadCheckins = async () => {
    try {
      const { data } = await api.get("/checkins");
      setCheckins(data || []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data: cfg } = await api.get("/config");
        setWhatsapp(cfg.whatsapp_number || "");
        const { data } = await api.get("/assessments/latest");
        setAssessment(data);
        try {
          const { data: rec } = await api.get("/recommendations");
          setRecs(rec.recommendations || []);
        } catch {
          /* ignore */
        }
        await loadCheckins();
      } catch {
        // No assessment yet
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center text-emerald-700" data-testid="dashboard-loading">
        Loading your dashboard…
      </main>
    );
  }

  if (!assessment) {
    return (
      <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-12" data-testid="dashboard-empty">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light tracking-tight">
            Hi {user.name}, let's start your journey.
          </h1>
          <p className="mt-3 text-emerald-900/70">
            You haven't completed your health assessment yet. It takes only 3 minutes.
          </p>
          <Button
            onClick={() => navigate("/questionnaire")}
            className="mt-6 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6"
            data-testid="dashboard-start-assessment"
          >
            Start my assessment <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </main>
    );
  }

  const waMsg = encodeURIComponent("Hi, I completed my health assessment. I would like guidance based on my results.");
  const waUrl = whatsapp ? `https://wa.me/${whatsapp}?text=${waMsg}` : `https://wa.me/?text=${waMsg}`;

  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-10" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              Dashboard
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light tracking-tight mt-1">
              Hi {user.name} 👋
            </h1>
            <p className="text-emerald-900/70 mt-1">
              Your latest health snapshot — keep going, you're doing great.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/questionnaire")}
              variant="outline"
              className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50"
              data-testid="dashboard-retake-btn"
            >
              Retake assessment
            </Button>
            <Link to="/report" data-testid="dashboard-view-report-btn">
              <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white">
                View full report <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Card className="rounded-2xl border-emerald-100 md:col-span-1">
            <CardContent className="p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">
                BMI
              </div>
              <BMIGauge bmi={assessment.bmi} category={assessment.bmi_category} />
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-emerald-100 md:col-span-2">
            <CardContent className="p-6">
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">
                Health summary
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-emerald-50/60 p-4">
                  <div className="text-xs text-emerald-900/60">Age</div>
                  <div className="font-display text-2xl text-emerald-950 mt-1">{assessment.age}</div>
                </div>
                <div className="rounded-xl bg-emerald-50/60 p-4">
                  <div className="text-xs text-emerald-900/60">Activity</div>
                  <div className="font-display text-2xl text-emerald-950 capitalize mt-1">
                    {assessment.activity_level?.replace("_", " ")}
                  </div>
                </div>
                <div className="rounded-xl bg-emerald-50/60 p-4">
                  <div className="text-xs text-emerald-900/60">Conditions</div>
                  <div className="font-display text-2xl text-emerald-950 mt-1">
                    {assessment.conditions?.length || 0}
                  </div>
                </div>
              </div>
              <div className="mt-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">
                  Risk indicators
                </div>
                <div className="flex flex-wrap gap-2">
                  {assessment.risk_indicators?.map((r) => (
                    <span
                      key={r.key}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${levelCls[r.level]}`}
                      data-testid={`dashboard-risk-${r.key}`}
                    >
                      {r.name}: {r.level}
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress + check-in */}
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <ProgressChart checkins={checkins} />
            <CheckinForm onCreated={loadCheckins} />
          </div>
          <div className="lg:col-span-2 space-y-6">
            <RecommendedReading limit={3} />
            <ReferralCard />
          </div>
        </div>

        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                  Top recommendations
                </div>
                <h2 className="font-display text-xl text-emerald-950 mt-1">
                  Chosen for your profile
                </h2>
              </div>
              <Link to="/report" className="text-sm text-emerald-700 font-semibold hover:underline" data-testid="dashboard-see-all-recs">
                See details →
              </Link>
            </div>
            {recs.length === 0 ? (
              <div className="text-sm text-emerald-900/70">No recommendations yet.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {recs.map((r) => (
                  <div
                    key={r.product.id}
                    className="rounded-xl border border-emerald-100 p-4 bg-white"
                    data-testid={`dashboard-rec-${r.product.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
                        <Sparkles className="w-4 h-4" />
                      </span>
                      <div className="font-medium text-emerald-950 text-sm line-clamp-1">
                        {r.product.name}
                      </div>
                    </div>
                    <div className="text-xs text-emerald-900/70 line-clamp-3">{r.why}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-amber-200 bg-amber-50/70">
          <CardContent className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-xl text-emerald-950">Get guidance from a real expert</h3>
              <p className="text-sm text-emerald-900/70 mt-1">
                Share your report with a certified coach on WhatsApp and get a live next step.
              </p>
            </div>
            <a href={waUrl} target="_blank" rel="noreferrer noopener" data-testid="dashboard-whatsapp-cta" onClick={() => track.whatsappClick("dashboard", null)}>
              <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6">
                <MessageCircle className="w-4 h-4 mr-2" /> Talk to an expert
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
